import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, AlertOctagon, Activity, Radio, Lock, Unlock, 
  Wifi, Search, Globe, ShieldQuestion, Server
} from 'lucide-react';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';

const MOCK_TRAFFIC = [
  { time: '00:00', safe: 154, risk: 2 },
  { time: '04:00', safe: 120, risk: 1 },
  { time: '08:00', safe: 450, risk: 12 },
  { time: '12:00', safe: 680, risk: 45 },
  { time: '16:00', safe: 930, risk: 62 },
  { time: '20:00', safe: 540, risk: 14 },
  { time: '24:00', safe: 210, risk: 3 },
];

const MOCK_THREATS = [
  { ip: '103.11.23.1', vector: 'SQL Injection Attempt', db: 'HS_QuocGia', risk: 'Critical', time: '10p trước', locked: true },
  { ip: '45.72.112.99', vector: 'DDoS / Syn Flood', db: 'API_Gateway', risk: 'High', time: '2h trước', locked: true },
  { ip: '118.23.4.10', vector: 'Unauthorized Token', db: 'Ident_Server', risk: 'Medium', time: '3h trước', locked: false },
  { ip: '27.72.100.12', vector: 'TLS Downgrade Attack', db: 'Sync_Node_02', risk: 'Low', time: '5h trước', locked: false },
];

export default function SecurityAuditPage() {
  useEffect(() => {
    console.log('SecurityAuditPage component mounted');
  }, []);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative pb-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
         <div>
            <h2 className="text-3xl font-heading font-semibold text-slate-900 dark:text-white tracking-tight flex items-center gap-3 leading-tight">
              <Shield className="text-rose-600 dark:text-rose-400" size={32} />
              An ninh Hạ tầng & Kết nối
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-2xl font-light tracking-tight text-xs leading-relaxed">
              Giám sát lưu lượng truy cập, bảo vệ các Endpoints và phân tích hành vi xâm nhập trái phép (IDS/IPS) trên mạng nội bộ quốc gia.
            </p>
         </div>
         <div className="flex gap-3">
            <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 text-[10px] font-semibold tracking-widest hover:bg-slate-50 dark:hover:bg-white/5 transition-all shadow-sm active:scale-95">
               <Globe size={18} /> Map View
            </button>
            <button className="relative overflow-hidden px-6 py-2.5 rounded-xl text-[10px] font-semibold tracking-[0.2em] transition-all duration-300 flex items-center gap-2 bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 hover:-translate-y-0.5 active:scale-95">
               <Radio size={18} className="animate-pulse" />
               Live SOC Mode
            </button>
         </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Critical Threats', value: '12', sub: '+3 hôm nay', icon: AlertOctagon, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10' },
          { label: 'IP Bị Chặn (Blocked)', value: '8,401', sub: 'Tổng số bản ghi', icon: Shield, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Băng thông mã hóa', value: '99.8%', sub: 'TLS v1.3 Standard', icon: Activity, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-500/10' },
          { label: 'SSL Hết hạn', value: '2', sub: 'Cần cập nhật ngay', icon: Server, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' },
        ].map((stat, idx) => (
          <div key={idx} className="glass-panel p-6 flex flex-col justify-between h-36 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm relative overflow-hidden group rounded-[2rem]">
            <div className={`absolute -right-10 -top-10 w-24 h-24 ${stat.bg} rounded-full blur-[40px] opacity-40`} />
            <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 tracking-widest flex items-center gap-2 relative z-10">
              <stat.icon size={16} className={stat.color} />
              {stat.label}
            </p>
            <div className="relative z-10 mt-2">
              <div className={`text-4xl font-heading font-semibold ${stat.color} tracking-tighter drop-shadow-sm dark:drop-shadow-[0_0_15px_currentColor] opacity-90`}>
                {stat.value} 
              </div>
              <div className="text-[9px] font-semibold text-slate-400 dark:text-slate-600 tracking-widest mt-1">{stat.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* CHART CONTAINER */}
        <div className="glass-panel p-8 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm relative overflow-hidden rounded-[2.5rem]">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/[0.02] blur-[100px] rounded-full" />
          <h3 className="text-[11px] font-semibold text-slate-900 dark:text-white mb-8 flex items-center gap-3 tracking-[0.2em] relative z-10">
            <Wifi size={20} className="text-indigo-600 dark:text-indigo-400" />
            Trạng thái Lưu lượng & Xung đột
          </h3>
          <div className="h-[300px] relative z-10">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={MOCK_TRAFFIC}>
                 <defs>
                   <linearGradient id="safe" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                     <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                   </linearGradient>
                   <linearGradient id="risk" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4}/>
                     <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                   </linearGradient>
                 </defs>
                 <XAxis dataKey="time" stroke="rgba(100,116,139,0.2)" tick={{fill: '#64748b', fontSize: 10, fontWeight: 300}} dy={10} axisLine={false} tickLine={false} />
                 <Tooltip 
                   contentStyle={{ backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,0,0,0.05)', borderRadius: '16px', fontSize: '12px', fontWeight: '300', color: '#10101a' }}
                   itemStyle={{ color: '#10101a' }}
                 />
                 <Area type="monotone" dataKey="safe" stroke="#6366f1" strokeWidth={4} fillOpacity={1} fill="url(#safe)" activeDot={{ r: 6, strokeWidth: 0 }} />
                 <Area type="monotone" dataKey="risk" stroke="#f43f5e" strokeWidth={4} fillOpacity={1} fill="url(#risk)" activeDot={{ r: 6, strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* THREAT LOGS CONTAINER */}
        <div className="glass-panel p-8 h-full flex flex-col border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm rounded-[2.5rem]">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <h3 className="text-[11px] font-semibold text-slate-900 dark:text-white flex items-center gap-3 tracking-[0.2em]">
               <ShieldQuestion size={20} className="text-rose-600 dark:text-rose-400" />
               Log Tấn công Thời gian thực
            </h3>
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-xl px-4 py-2 shadow-inner">
               <Search size={14} className="text-slate-400" />
               <input type="text" placeholder="Tìm IP, Loại..." className="bg-transparent text-[10px] font-semibold outline-none w-32 text-slate-900 dark:text-white" />
            </div>
          </div>
          
          <div className="space-y-4 flex-1">
             {MOCK_THREATS.map((threat, idx) => (
                <motion.div 
                   initial={{ opacity: 0, x: 20 }}
                   animate={{ opacity: 1, x: 0 }}
                   transition={{ delay: idx * 0.15 }}
                   key={idx} 
                   className="p-5 rounded-3xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5 hover:border-indigo-500/30 transition-all flex items-center justify-between group shadow-sm"
                >
                   <div className="flex items-start gap-5">
                      <div className={`mt-0.5 p-2.5 rounded-2xl shadow-inner ${threat.risk === 'Critical' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400' : threat.risk === 'High' ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
                         <AlertOctagon size={20} />
                      </div>
                      <div>
                         <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-slate-900 dark:text-white font-mono tracking-tighter">{threat.ip}</span>
                            <span className="text-[9px] font-semibold bg-white dark:bg-black/40 text-slate-500 dark:text-slate-500 px-2 py-0.5 rounded-lg border border-slate-100 dark:border-white/10 tracking-widest">{threat.db}</span>
                         </div>
                         <p className={`text-[10px] font-semibold tracking-tight ${threat.risk === 'Critical' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'}`}>
                            {threat.vector}
                         </p>
                      </div>
                   </div>

                   <div className="text-right flex flex-col items-end gap-2.5">
                      <span className="text-[9px] font-semibold text-slate-400 dark:text-slate-600 tracking-[0.2em]">{threat.time}</span>
                      {threat.locked ? (
                         <div className="flex items-center gap-1.5 text-[9px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 px-3 py-1.5 rounded-xl border border-indigo-500/20 tracking-widest shadow-sm">
                            <Lock size={12} /> <span className="hidden md:inline">Blocked by SOC</span>
                         </div>
                      ) : (
                         <button className="flex items-center gap-1.5 text-[9px] font-semibold text-slate-500 hover:text-indigo-600 dark:text-slate-600 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 px-3 py-1.5 rounded-xl transition-all border border-slate-100 dark:border-white/5 tracking-widest shadow-sm">
                            <Unlock size={12} /> Bỏ qua
                         </button>
                      )}
                   </div>
                </motion.div>
             ))}
          </div>

          <button className="w-full mt-6 py-3 border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02] hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-[10px] font-semibold tracking-[0.3em] rounded-2xl transition-all shadow-sm active:scale-95">
             Xem Toàn bộ Log An Ninh
          </button>
        </div>
      </div>
    </div>
  );
}
